import React from "react";
import { object, bool, func, array } from "prop-types";
import { connect } from "react-redux";
import get from "lodash/get";
import flatten from "lodash/flatten";
import { Link, StoryElement } from "@quintype/components";

import Headline from "../../atoms/headline";
import PdfIcon from "../../atoms/icons/pdf-icon";
import CardImage from "../../atoms/card-image";
import { LabelWithBackground } from "../../atoms/label";
import PublicationEntityImage from "../../atoms/publication-entity-image";
import StoryCard from "../story-card";

import { formatBytes } from "../../helper/utils";
import "./publication-story.m.css";

const PublicationStoryView = ({ story, isUserLoggedIn, navigateToPage, entityList }) => {
  const storyObj = story.story ? story.story : story;
  const isLoginAccess = storyObj.access === "login";
  const publicationYearObj = get(storyObj, ["metadata", "story-attributes", "publicationyear", "0"], null);
  const publicationTypeObj = get(storyObj, ["metadata", "story-attributes", "publicationtype", "0"], null);
  const elementAttachmentList =
    storyObj.cards &&
    storyObj.cards.length &&
    storyObj.cards.map(card => card["story-elements"].filter(storyElement => storyElement.subtype === "attachment"));

  const elementSummaryList =
    storyObj.cards &&
    storyObj.cards.length &&
    storyObj.cards
      .map(card => card["story-elements"].find(storyElement => storyElement.subtype === "summary"))
      .filter(item => item);

  const tagList = [];
  let attachmentObj = null;

  if (elementAttachmentList && elementAttachmentList.length) {
    const attachmentList = flatten(elementAttachmentList);

    if (attachmentList && attachmentList.length) {
      attachmentObj = attachmentList[0];
      const size = get(attachmentObj, ["metadata", "file-size"], 0);
      // Calculate the size in human readable format
      attachmentObj.fileSize = size ? formatBytes(size) : size;
    }
  }

  // 1st tag should be publication type
  if (publicationTypeObj && publicationTypeObj.type === "publication-type") {
    tagList.push({ id: publicationTypeObj.id, name: publicationTypeObj.name });
  } else if (publicationTypeObj && publicationTypeObj.type === "Publication Type") {
    tagList.push({ id: publicationTypeObj.id, name: publicationTypeObj.name });
  }

  // 2nd tag should be publication year
  if (publicationYearObj && publicationYearObj.type === "publication-year") {
    tagList.push({ id: publicationYearObj.id, name: publicationYearObj.name });
  } else if (publicationYearObj && publicationYearObj.type === "Publication Year") {
    tagList.push({ id: publicationYearObj.id, name: publicationYearObj.name });
  }

  const tags = tagList.map(tag => <LabelWithBackground key={tag.id} text={tag.name} />);

  const authors =
    storyObj.authors &&
    storyObj.authors.filter(c => c.name).map((author, index) => (
      <Link key={index} href={`/author/${author.id}/${author.slug}`} styleName="links">
        <span>{author.name}</span>
      </Link>
    ));

  const summaryView = () => {
    if (elementSummaryList && elementSummaryList.length) {
      const summaryObj = elementSummaryList[0];

      return <StoryElement element={summaryObj} story={story} />;
    }

    return null;
  };

  const imageView = () => {
    let storyHeroImage = null;

    // If story hero image doesn't exist then pick from entity image
    if (storyObj["hero-image-s3-key"]) {
      storyHeroImage = <CardImage story={storyObj} defaultWidth={36} aspectRatio={[1, 1]} imageType="image1x1" />;
    } else {
      // Only if Publication Type exists pick the image
      if (publicationTypeObj) {
        const publicationTypeEntity = entityList.find(c => c.id === publicationTypeObj.id);

        if (publicationTypeEntity) {
          storyHeroImage = (
            <PublicationEntityImage
              entity={publicationTypeEntity}
              defaultWidth={36}
              widths={[300]}
              aspectRatio={[1, 2]}
              imageType="image1x2"
            />
          );
        }
      }
    }

    return storyHeroImage;
  };

  // const downloadClick = () => {
  //   if (isUserLoggedIn) {
  //     window.open(attachmentObj.url, "_blank");
  //   } else {
  //     navigateToPage("/signin");
  //   }
  // };

  return (
    <div className="story-grid">
      <div styleName="publication-story" className="container">
        {/* Left Side Content */}
        <div styleName="left-content">
          {imageView()}

          {/* Tags */}
          {tags.length ? <div styleName="tags">{tags}</div> : null}

          {/* Authors */}
          {authors.length ? <div styleName="authors">{authors}</div> : null}

          {/* Download Attachment */}
          {attachmentObj ? (
            isLoginAccess ? (
              // <div styleName="details">Not avaiable for download</div>
              null
            ) : (
              <a href={attachmentObj.url} target="_blank" rel="noopener noreferrer" styleName="attachment-link">
                <PdfIcon />
                <span styleName="details">Download {attachmentObj.fileSize ? `(${attachmentObj.fileSize})` : ""}</span>
              </a>
            )
          ) : null}
        </div>

        {/* Right Side Content */}
        <div styleName="right-content">
          <Headline text={storyObj.headline} headerType={9} headerLevel={2} />

          {story.cards.map(card => (
            <StoryCard key={card.id} card={card} story={story} showAttachment={false} isPublicationStory={true} />
          ))}

          {/* Summary */}
          {summaryView()}
        </div>
      </div>
    </div>
  );
};

PublicationStoryView.propTypes = {
  story: object,
  isUserLoggedIn: bool,
  navigateToPage: func,
  entityList: array
};

const mapStateToProps = state => {
  return {
    isUserLoggedIn: get(state, ["authReducer", "isUserLoggedIn"], false),
    entityList: get(state, ["entityReducer"], [])
  };
};

const mapDispatchToProps = dispatch => ({
  navigateToPage: url => global.app.navigateToPage(dispatch, url)
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(PublicationStoryView);
